import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import { requestVehicleReturn } from "../../services/vehicleServices.ts";
import placeholder from "../../assets/Car-place-holder-PNG.png";
import { FaEye, FaEdit, FaUndoAlt  } from "react-icons/fa";
import { useLanguage } from "../../context/LanguageContext";
import { useToast } from "../../context/ToastContext";

// --- START: Modal Component (No changes needed here) ---
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  vehicleName,
  isSubmitting,
}) => {
  const { translate } = useLanguage();
  if (!isOpen) return null;

  return (
    // Fixed overlay/backdrop for the modal
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      aria-modal="true"
      role="dialog"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {translate("myVehicles.requestReturn.requestVehicleReturn")}
        </h3>
        <p className="text-gray-700 mb-6">
          {translate("myVehicles.requestReturn.returnRequestMessage1")}
          <span className="font-bold text-red-600"> "{vehicleName}"</span>
          {translate("myVehicles.requestReturn.returnRequestMessage2")}
          </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            {translate("myVehicles.requestReturn.close")}
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {isSubmitting
              ? "Requesting..."
              : translate("myVehicles.requestReturn.request")}
          </button>
        </div>
      </div>
    </div>
  );
};
// --- END: Modal Component ---

// Commit Testing 

const VehicleListCard = ({ vehicle }) => {
  const {
    id, // <--- This is the vehicleId you need to use
    title,
    slug,
    lotNo,
    vin,
    miles,
    buyNowPrice,
    reservedPrice,
    soldPrice,
    cylinder,
    fuelType,
    engine,
    image,
    highlights = [],
    ProductStatus,
    rejectionReason = "",
    auctionName = "",
    returnRequestStatus = null,
    activeFilter = "all",
  } = vehicle;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState(
    Boolean(returnRequestStatus) && returnRequestStatus !== "Rejected",
  );
  const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);
  const navigate = useNavigate();
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const { translate } = useLanguage();

  const { showSuccess, showError } = useToast();

  useEffect(() => {
    setIsRequestSubmitted(
      Boolean(returnRequestStatus) && returnRequestStatus !== "Rejected",
    );
  }, [returnRequestStatus]);


  /**
   * CORRECTED: Made the function async to use await, and added error handling.
   * Passes the destructured 'id' as the vehicleId.
   */
const handleConfirmReturn = async () => {
    if (isRequestSubmitted || isSubmittingReturn) return;

    // Close the modal immediately for better user experience
    setIsModalOpen(false);
    setIsSubmittingReturn(true);

    try {
      // 1. Send request
      const response = await requestVehicleReturn({ vehicleId: id });
      
      // 2. Instantly disable button on success
      setIsRequestSubmitted(true);
      if (showSuccess) showSuccess("Return request submitted successfully.");
      
    } catch (error) {
      console.error("Error requesting vehicle return:", error);
      
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        "";

      // 3. IF BACKEND SAYS IT ALREADY EXISTS: Silently disable the button!
      if (message.toLowerCase().includes("already exists")) {
        setIsRequestSubmitted(true); 
      } else {
        // Only show a red error popup for actual connection failures
        if (showError) showError(message || "Failed to request return. Please try again.");
      }
    } finally {
      setIsSubmittingReturn(false);
    }
  };

  return (
    <>
      {/* Mobile Card View (visible on screen widths < md) */}
      <div className="md:hidden p-4 border-b border-default hover:bg-default/35 transition-colors duration-200 space-y-3">
        {/* Top Section: Image + Basic Info */}
        <div className="flex gap-3">
          {/* Image */}
          <div className="w-20 h-16 flex-shrink-0">
            <img
              src={image || placeholder}
              alt={title}
              className="w-full h-full object-cover rounded border border-default"
              onError={(e) => {
                e.target.src = placeholder;
              }}
            />
          </div>
          {/* Details */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-sm line-clamp-1 mb-1">
              {title}
            </h3>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/70">
              <div>
                Lot: <span className="font-semibold text-white">{lotNo}</span>
              </div>
              <div>
                VIN: <span className="font-semibold text-white">{vin || "N/A"}</span>
              </div>
            </div>
            {/* Highlights */}
            {highlights.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {highlights.slice(0, 2).map((highlight, i) => {
                  const isObject = typeof highlight === "object" && highlight !== null;
                  const name = isObject ? highlight.name : highlight;
                  const bgColor = isObject ? highlight.bgColor : "#f97316";
                  const textColor = isObject ? highlight.textColor : "#fff";

                  return (
                    <span
                      key={i}
                      className="px-2 py-[2px] rounded text-[10px] font-medium shadow-sm"
                      style={{
                        backgroundColor: bgColor,
                        color: textColor,
                      }}
                    >
                      {name}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Specs Grid */}
        {ProductStatus !== "Rejected" && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs border-t border-default/50 pt-2.5">
            <div className="flex justify-between border-b border-default/35 pb-1">
              <span className="text-white/60">Mileage:</span>
              <span className="text-white font-medium">{miles?.toLocaleString() || "-"}</span>
            </div>
            <div className="flex justify-between border-b border-default/35 pb-1">
              <span className="text-white/60">Fuel:</span>
              <span className="text-white font-medium">{fuelType || "-"}</span>
            </div>
            <div className="flex justify-between border-b border-default/35 pb-1">
              <span className="text-white/60">Cylinder:</span>
              <span className="text-white font-medium">{cylinder || "-"}</span>
            </div>
            <div className="flex justify-between border-b border-default/35 pb-1">
              <span className="text-white/60">Engine:</span>
              <span className="text-white font-medium">{engine || "-"}</span>
            </div>
            <div className="col-span-2 flex justify-between border-b border-default/35 pb-1">
              <span className="text-white/60">Updated At:</span>
              <span className="text-white font-medium">
                {vehicle.updatedAt ? new Date(vehicle.updatedAt).toLocaleDateString() : "-"}
              </span>
            </div>
          </div>
        )}

        {/* Pricing Info */}
        <div className="flex flex-wrap gap-4 text-xs bg-default/40 p-2 rounded border border-default/30">
          {activeFilter === "all" ? (
            <>
              <div>
                <span className="text-white/60 mr-1">Reserve Price:</span>
                <span className="text-white font-semibold">
                  {displayPriceWithSymbol(convertPrice(Number(reservedPrice || 0)))}
                </span>
              </div>
              <div>
                <span className="text-white/60 mr-1">BuyNow Price:</span>
                <span className="text-white font-semibold">
                  {buyNowPrice ? displayPriceWithSymbol(convertPrice(Number(buyNowPrice))) : "-"}
                </span>
              </div>
            </>
          ) : activeFilter === "sold" ? (
            <div>
              <span className="text-white/60 mr-1">Sold Price:</span>
              <span className="text-orange-500 font-bold">
                {displayPriceWithSymbol(convertPrice(Number(soldPrice || 0)))}
              </span>
            </div>
          ) : activeFilter === "buy-now" ? (
            <div>
              <span className="text-white/60 mr-1">BuyNow Price:</span>
              <span className="text-white font-semibold">
                {buyNowPrice ? displayPriceWithSymbol(convertPrice(Number(buyNowPrice))) : "-"}
              </span>
            </div>
          ) : (
            <div>
              <span className="text-white/60 mr-1">Reserve Price:</span>
              <span className="text-white font-semibold">
                {displayPriceWithSymbol(convertPrice(Number(reservedPrice || 0)))}
              </span>
            </div>
          )}
        </div>

        {/* Rejection Reason (If applicable) */}
        {ProductStatus === "Rejected" && rejectionReason && (
          <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-xs text-red-400">
            <span className="font-semibold block mb-0.5">Rejection Reason:</span>
            {rejectionReason}
          </div>
        )}

        {/* Actions Row */}
        <div className="flex items-center justify-end gap-2 pt-1">
          {/* View Button */}
          <button
            onClick={() => navigate(`/${slug}`)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary-dark border border-default text-white rounded-md hover:bg-opacity-80 transition-all text-xs"
          >
            <FaEye size={13} />
            View
          </button>

          {/* Edit Button */}
          {(ProductStatus === "Waiting" ||
            ProductStatus?.name === "Waiting" ||
            ProductStatus === "Awaiting" ||
            ProductStatus?.name === "Awaiting" ||
            ProductStatus === "Rejected") &&
            !(
              ProductStatus === "Awaiting Arrival" ||
              ProductStatus?.name === "Awaiting Arrival"
            ) && (
              <button
                onClick={() => navigate(`/addvehicles?edit=${id}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary to-secondary text-white rounded-md hover:opacity-90 transition-all text-xs"
              >
                <FaEdit size={13} />
                Edit
              </button>
            )}

          {/* Request Return Button */}
        {/* Request Return Button */}
{ProductStatus === "Stock" && activeFilter !== "returned" && ProductStatus !== "Returned" && (
  <button
    onClick={() => {
      if (!isRequestSubmitted && !isSubmittingReturn) {
        setIsModalOpen(true);
      }
    }}
    disabled={isRequestSubmitted || isSubmittingReturn}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all text-xs ${
      isRequestSubmitted || isSubmittingReturn
        ? "bg-gray-600 text-gray-300 border border-gray-400 cursor-not-allowed"
        : "bg-primary border border-primary text-white hover:bg-primary/80"
    }`}
  >
    <FaUndoAlt size={11} />
    {isSubmittingReturn
      ? "Requesting..."
      : isRequestSubmitted
      ? "Submitted"
      : "Request Return"}
  </button>
)}
        </div>
      </div>

      {/* Desktop Grid Row (visible on screen widths >= md) */}
      <div className="hidden md:grid grid-cols-12 gap-2 items-center px-2 py-1 hover:bg-default transition-colors duration-200">
        {/* Vehicle Image */}
        <div className="col-span-1">
          <img
            src={image || placeholder}
            alt={title}
            className="w-16 h-12 object-cover rounded"
            onError={(e) => {
              e.target.src = placeholder;
            }}
          />
        </div>
        {/* Vehicle Info */}
        <div className="col-span-2">
          <h3 className="font-semibold text-white text-sm truncate mb-1">
            {title}
          </h3>
          <div className="text-xs text-white/90 mb-1">
            Lot: <span className="font-medium">{lotNo}</span>
          </div>
          <div className="text-xs text-white/90 truncate mb-2">
            VIN: {vin ? `${vin}` : "N/A"}
          </div>

          {/* Highlights */}
          {highlights.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {highlights.slice(0, 2).map((highlight, i) => {
                const isObject =
                  typeof highlight === "object" && highlight !== null;
                const name = isObject ? highlight.name : highlight;
                const bgColor = isObject ? highlight.bgColor : "#f97316";
                const textColor = isObject ? highlight.textColor : "#fff";

                return (
                  <span
                    key={i}
                    className="px-2 py-[2px] rounded text-[10px] font-medium shadow-sm"
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                    }}
                  >
                    {name}
                  </span>
                );
              })}
              {highlights.length > 2 && (
                <span className="bg-gray-600 text-white px-2 py-[2px] rounded text-[10px] font-medium shadow-sm">
                  +{highlights.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        {/* Price cell(s) — vary by active tab */}
        {activeFilter === "all" ? (
          <>
            {/* Reserved Price */}
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">
                {displayPriceWithSymbol(
                  convertPrice(Number(reservedPrice || 0)),
                )}
              </span>
            </div>
            {/* Buy Now Price */}
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">
                {buyNowPrice
                  ? displayPriceWithSymbol(convertPrice(Number(buyNowPrice)))
                  : "-"}
              </span>
            </div>
          </>
        ) : activeFilter === "sold" ? (
          <div className="col-span-1 text-xs text-white text-left">
            <span className="font-medium">
              {displayPriceWithSymbol(
                convertPrice(Number(soldPrice || 0)),
              )}
            </span>
          </div>
        ) : activeFilter === "buy-now" ? (
          <div className="col-span-1 text-xs text-white text-left">
            <span className="font-medium">
              {buyNowPrice
                ? displayPriceWithSymbol(convertPrice(Number(buyNowPrice)))
                : "-"}
            </span>
          </div>
        ) : (
          /* Default: auction, stock, returned, under-approval, waiting, awaiting-arrival, rejected */
          <div className="col-span-1 text-xs text-white text-left">
            <span className="font-medium">
              {displayPriceWithSymbol(
                convertPrice(Number(reservedPrice || 0)),
              )}
            </span>
          </div>
        )}
        {ProductStatus === "Rejected" ? (
          <>
            {/* FIXED: Reason takes 6 columns to fill the space properly */}
         <div className="col-span-5 text-sm text-red-500 text-left pr-2 overflow-hidden">
              <span className="font-medium text-sm line-clamp-2">
                {rejectionReason || "-"}
              </span>
            </div>
            {/* Actions inline with rejection reason */}
            <div className="col-span-2 col-start-11 flex items-center justify-end gap-2 pr-4">
              {/* View Button */}
              <button
                onClick={() => navigate(`/${slug}`)}
                className="h-9 w-9 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm"
                title={translate("myVehicles.requestReturn.viewDetails")}
              >
                <FaEye size={14} />
              </button>

              {/* Edit Button */}
              <button
                onClick={() => navigate(`/addvehicles?edit=${id}`)}
                className="h-9 w-9 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm"
                title={translate("myVehicles.requestReturn.editVehicle")}
              >
                <FaEdit size={14} />
              </button>
            </div>
          </>
        ) : (
          <>
            
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">
                {miles?.toLocaleString() || "-"}
              </span>
            </div>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">{fuelType || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">{cylinder || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">{engine || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">
                {vehicle.updatedAt
                  ? new Date(vehicle.updatedAt).toLocaleDateString()
                  : "-"}
              </span>
            </div>
           
           {/* Actions */}
            <div className="col-span-2 col-start-11 flex items-center justify-end gap-2 pr-4">
              {/* View Button */}
              <button
                onClick={() => navigate(`/${slug}`)}
                className="h-9 w-9 flex items-center justify-center bg-secondary-dark border border-default text-white rounded-lg hover:bg-opacity-80 transition-all duration-300 ease-in-out shadow-sm shrink-0"
                title={translate("myVehicles.requestReturn.viewDetails")}
              >
                <FaEye size={16} />
              </button>

{/* Request Return Button - Icon that expands on hover */}
{activeFilter !== "returned" && ProductStatus !== "Returned" && ProductStatus !== "Sold" && (
  <div className="relative group">
    <button
      onClick={() => {
        if (!isRequestSubmitted && !isSubmittingReturn) {
          setIsModalOpen(true);
        }
      }}
      disabled={isRequestSubmitted || isSubmittingReturn}
      className={`flex items-center justify-center h-9 w-9 rounded-lg shadow-sm shrink-0 ${
        isRequestSubmitted || isSubmittingReturn
          ? "border border-gray-400 text-gray-300 bg-gray-600 cursor-not-allowed"
          : "border border-primary text-white bg-primary hover:bg-primary/80"
      }`}
    >
      <FaUndoAlt className="w-4 h-4" />
    </button>

    {/* Tooltip */}
    <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
      {isSubmittingReturn
        ? "Requesting..."
        : isRequestSubmitted
        ? "Submitted"
        : translate("myVehicles.requestReturn.requestReturn") || "Request Return"}
    </div>
  </div>
)}
              {/* Edit button */}
              {(ProductStatus === "Waiting" ||
                ProductStatus?.name === "Waiting" ||
                ProductStatus === "Awaiting" ||
                ProductStatus?.name === "Awaiting") &&
                !(
                  ProductStatus === "Awaiting Arrival" ||
                  ProductStatus?.name === "Awaiting Arrival"
                ) && (
                  <button
                    onClick={() => navigate(`/addvehicles?edit=${id}`)}
                    className="h-9 w-9 flex items-center justify-center bg-gradient-to-r from-primary to-secondary border border-default text-white rounded-lg hover:opacity-80 transition-all duration-300 ease-in-out shadow-sm shrink-0"
                    title="Edit Vehicle"
                  >
                    <FaEdit size={16} />
                  </button>
                )}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmReturn} // Calls the async function
        vehicleName={title}
        isSubmitting={isSubmittingReturn}
      />
    </>
  );
};

export default VehicleListCard;
